import styled from "styled-components";

import { DonutChart } from "@similarweb/pro-logic-components";
import { CircularLoader } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { useMatchMedia } from "app/common/hooks/useMatchMedia";
import { GenericEmptyState } from "app/components/React/empty-state/GenericEmptyState";
import { useTranslation } from "pro-features/components/WithTranslation";

import { WidgetCard } from "../WidgetCard";

import { smallGraphConfig, largeGraphConfig } from "./configs/graph";
import { Legend } from "./Legend";
import { useChartData } from "./useChartData";

const MEDIA_QUERY_LARGE = "screen and (min-width: 1440px)";

interface IResponsiveProps {
    responsive?: boolean;
}

const FixedSizeWidgetCard = styled(WidgetCard)<IResponsiveProps>`
    max-width: 396px;

    ${({ responsive }) =>
        responsive
            ? `@media ${MEDIA_QUERY_LARGE} {
                max-width: 504px;
            }`
            : ""}
`;

const MyChannelsOverviewWidget = ({ responsive }: IResponsiveProps) => {
    const translate = useTranslation();
    const { data, isLoading } = useChartData();
    const isLargeScreen = useMatchMedia(MEDIA_QUERY_LARGE);
    const graphConfig = responsive && isLargeScreen ? largeGraphConfig : smallGraphConfig;

    if (isLoading) {
        return <CircularLoader isLarge />;
    }

    if (!data) {
        return <GenericEmptyState title={translate("global.nodata.notavilable")} />;
    }

    return (
        <Flex
            gap="22px"
            width="100%"
            alignItems="center"
            padding="0px 6px 0px 16px"
        >
            <DonutChart
                innerSize="60%"
                config={graphConfig}
                data={data}
                shouldPresentLegend={false}
            />
            <Legend chartData={data[0].data} />
        </Flex>
    );
};

export const MyChannelsOverview = ({ responsive }: IResponsiveProps) => {
    const translate = useTranslation();

    return (
        <FixedSizeWidgetCard
            title={translate("bt.report.channels.my-channels-overview.title")}
            tooltip={translate("bt.report.channels.my-channels-overview.tooltip")}
            automationIds={{
                title: "my-channels-overview-title",
                body: "my-channels-overview-body",
            }}
            responsive={responsive}
        >
            <Flex
                padding="16px"
                flexGrow="1"
                alignItems="center"
                justifyContent="center"
                height="100%"
            >
                <MyChannelsOverviewWidget responsive={responsive} />
            </Flex>
        </FixedSizeWidgetCard>
    );
};
